"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowLeft, ArrowRight, MapPin, Calendar, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function PermanentCollectionConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const scrollByAmount = (direction: "left" | "right") => {
    const node = scrollRef.current;
    if (!node) return;
    const amount = Math.min(node.clientWidth * 0.8, 720);
    node.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth"
    });
  };
  return <section id="featured-expeditions" className="bg-background text-foreground py-12 lg:py-18 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 animate-fade-in-up">
          <div className="max-w-2xl">
            <span className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4 block">
              Curated Travel Experiences
            </span>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.1] tracking-tight">
              Journeys <span className="italic text-secondary">designed</span> for the soul.
            </h2>
            <p className="font-serif text-lg md:text-xl text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Explore our handpicked selection of global escapes and domestic solo adventures, crafted for the modern explorer.
            </p>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" size="icon" aria-label="Scroll previous" onClick={() => scrollByAmount("left")} className="h-12 w-12 rounded-lg border-primary/10 bg-card shadow-md hover:bg-primary hover:text-white transition-all duration-300">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button type="button" variant="outline" size="icon" aria-label="Scroll next" onClick={() => scrollByAmount("right")} className="h-12 w-12 rounded-lg border-primary/10 bg-card shadow-md hover:bg-primary hover:text-white transition-all duration-300">
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-16 md:mt-24">
        {loading ? <div className="mx-auto max-w-[1440px] pl-5 md:pl-8 lg:pl-16">
            <div className="flex gap-8 overflow-hidden">
              {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="shrink-0 w-[300px] md:w-[400px]">
                  <div className="aspect-[4/5] w-full rounded-lg bg-muted animate-pulse shadow-lg" />
                  <div className="mt-6 h-6 w-2/3 rounded-md bg-muted animate-pulse" />
                  <div className="mt-3 h-4 w-1/3 rounded-md bg-muted animate-pulse" />
                </div>)}
            </div>
          </div> : error ? <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-16 text-center py-12">
            <p className="font-serif text-lg text-muted-foreground mb-6">
              Unable to load our current expeditions.
            </p>
            <Button type="button" variant="outline" onClick={refetch} className="rounded-lg border-primary text-primary hover:bg-primary hover:text-white">
              Retry Loading
            </Button>
          </div> : products.length === 0 ? <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-16 text-center py-12">
            <p className="font-serif text-lg text-muted-foreground">
              No active departures at this moment. Check back soon for new horizons.
            </p>
          </div> : <div ref={scrollRef} className="overflow-x-auto overflow-y-hidden scroll-smooth no-scrollbar" style={{
        scrollSnapType: "x mandatory",
        scrollbarWidth: "none",
        msOverflowStyle: "none"
      }}>
            <div className="flex gap-8 pr-5 md:pr-8 lg:pr-16" style={{
          paddingLeft: "max(1.25rem, calc((100vw - 1440px) / 2 + 4rem))",
          scrollPaddingLeft: "1.25rem"
        }}>
              {products.map((product, i) => <article key={product.id} data-index={i} className="group shrink-0 w-[300px] md:w-[420px] relative animate-slide-in-right hover:-translate-y-2 transition-transform duration-300" style={{
            scrollSnapAlign: "start"
          }}>
                  <Link to={product.link || "#"} className="block">
                    <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg shadow-lg transition-all duration-500 group-hover:shadow-2xl">
                      {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" /> : <div className="h-full w-full bg-muted flex items-center justify-center">
                          <MapPin className="h-12 w-12 text-muted-foreground/20" />
                        </div>}
                      
                      <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="absolute bottom-6 left-6 right-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <div className="flex items-center gap-4 text-white/90 text-xs font-sans uppercase tracking-widest">
                          <span className="flex items-center gap-1.5">
                            <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate> Seasonal
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Users className="h-3 w-3" /> Group/Private
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <h3 className="font-serif text-2xl md:text-3xl font-medium text-foreground group-hover:text-primary transition-colors duration-300">
                        {product.title}
                      </h3>
                      
                      <div className="flex items-center justify-between">
                        {product.price !== undefined && <div className="flex flex-col">
                            <span className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">Starting from</span>
                            <span className="font-serif text-xl text-primary font-semibold">
                              INR {Number(product.price).toLocaleString("en-IN")}
                            </span>
                          </div>}
                        <div className="h-px flex-grow mx-4 bg-border/50" />
                        <span className="font-sans text-xs font-bold uppercase tracking-tighter text-secondary group-hover:translate-x-1 transition-transform duration-300">
                          View Details →
                        </span>
                      </div>
                    </div>
                  </Link>

                  <div className="mt-4">
                    <AddToCartButton productId={product.id} variant="default" size="lg" text="Secure Your Spot" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all duration-300 py-6 text-base font-serif" />
                  </div>
                </article>)}
              <div aria-hidden="true" className="shrink-0 w-8" />
            </div>
          </div>}
      </div>
    </section>;
}
export default function Expeditions() {
  return <EcommerceProductsProvider wp_query={{
    per_page: 8,
    order: "desc",
    orderby: "date"
  }}>
      <PermanentCollectionConsumer />
    </EcommerceProductsProvider>;
}